import { Component, OnInit, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import * as Quill from 'quill';
import { Banner } from '../../../shared/models/Banner';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { AdminService } from '../../../admin/admin.service';
import { TCCPCommonService } from '../../../shared/services/tccp-common.service';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ManageBannerService } from '../../manage-banner/manage-banner.service';
import { Router } from '@angular/router';
import { TCCPConstantValues } from '../../../shared/models/tccp-constants';
import { MsalService } from '@azure/msal-angular';

@Component({
  selector: 'app-add-banner',
  templateUrl: './add-banner.component.html',
  styleUrls: ['./add-banner.component.scss']
})

export class AddBannerComponent implements OnInit {
  public pageTitle: string = null;
  public alerts: any[] = [];
  public bannerForm: FormGroup;
  public banner: Banner = < Banner > { title: null, content: null, contentType: 'internal', bannerType: 'multi' };
  public selectedContentType = 'internal';
  public mode = 'add';
  public initialBanner: Banner = < Banner > {};
  public editorOptions;
  public selectedApp = this.adminService.selectedApplication;
  public isViewMode = this.bannerService.pageMode === 'view';
  public isSubmit = false ;
  public selectedBanner: string = this.bannerService.selectedBanner;
  public editorStyle = {
    height: '200px',
    readonly: true
  };
  public categoryTypes = [
    { icon: 'fa fa-exclamation-triangle fa-3', name: 'General Message', value: 'General' },
    { icon: 'fa fa-exclamation-triangle fa-3', name: 'Alert Message', value: 'Alert' },
    { icon: 'fa fa-exclamation-triangle fa-3', name: 'Warning Message', value: 'Warning' }];

  public contentTypes = [{ name: 'Internal', value: 'Internal' }, { name: 'External', value: 'External' }, { name: 'Both', value: 'Both' }];
  public bannerTypes = [{ name: 'Single Line', value: 'single' }, { name: 'Multi Line', value: 'multi' }];

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog, private adminService: AdminService,
    private bannerService: ManageBannerService, public commonService: TCCPCommonService,
    private authService: MsalService, private router: Router
  ) {
    const fontNames = this.selectedApp === 'toyota' ? ['toyota-regular', 'toyota-semibold',
      'toyota-bold', 'toyota-light', 'toyota-black', 'toyota-book'] :
      ['nobel-regular', 'nobel-bold', 'nobel-light', 'nobel-book'];

    const Font = Quill.import('attributors/style/font');
    Font.whitelist = fontNames;
    Quill.register(Font, true);
    const toolbarOptions = [ // remove list and indent if this.isSingleLine= true;
      ['underline'],        // toggled buttons
      // custom button values
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],      // superscript/subscript
      [{ indent: '-1' }, { indent: '+1' }],          // outdent/indent

      // dropdown with defaults from theme
      [{ font: fontNames }]

    ];
    this.editorOptions = {
      toolbar: toolbarOptions
    };
  }

  ngOnInit() {
    this.pageTitle = this.bannerService.pageMode === 'add' ? 'Add Banner' : this.bannerService.pageMode === 'edit' ?
      'Edit Banner' : 'View Banner';
    this.banner = < Banner > {};
    this.bannerForm = this.fb.group({
      title: new FormControl(this.banner.title, [Validators.required]),
      content: new FormControl(this.banner.content, [Validators.required]),
      contentType: new FormControl('Internal', [Validators.required]),
      bannerType: new FormControl('multi', [Validators.required]),
      category: new FormControl(this.banner.category)
    });
    this.setPageMode();
  }

  ngAfterContentChecked(){
    this.autoSizeTextArea();
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable< boolean > | boolean {
    this.alerts = [];
    if ((this.mode === 'add' || this.mode === 'edit')) {
      const dialogRef = this.dialog.open(ConfirmModalComponent, {
        width: '350px',
        data: TCCPConstantValues.admin.messages.LOST_UNSAVED_MESSAGE,
        position: { top: '100px' },
        id: 'admin-' + this.adminService.selectedApplication
      });
      return dialogRef.afterClosed();
    } else {
      return true;
    }
  }

  public hasError = (controlName: string, errorName: string, groupName?: string) => {
    return this.isSubmit && this.bannerForm.controls[controlName].hasError(errorName);
  }

  setPageMode(mode?: string) {
    // this.loaderService.show();
    this.initialBanner = Object.assign({}, this.banner);
    this.mode = this.bannerService.pageMode;
    if (mode && mode !== undefined) {
      this.mode = mode;
    }
    this.pageTitle = this.mode === 'edit' ? 'Edit Banner' : this.mode === 'add' ? 'Add Banner' : 'View Banner';
    this.banner = < Banner > {};
    if ((this.mode === 'edit') || (this.mode === 'view')) {
      this.pageTitle = this.mode === 'edit' ? 'Edit Banner' : 'View Banner';
      this.getBanner();
      this.bannerForm.controls['title'].disable();
      if (this.mode === 'view') {
        this.bannerForm.disable();
      }
    } else {
      this.pageTitle = 'Add Banner';
    }
  }

  public showAlertMessages(messageType, message) {
    this.alerts = [];
    if (this.alerts.length === 0) {
      this.alerts.push({
        type: messageType,
        msg: message
      });
    }
    window.scroll(0, 0);
  }

  closeAlerts() {
    this.alerts = [];
  }

  getBanner(bannerId?: string) {
    this.bannerService.getBanner(this.selectedApp, this.selectedBanner['status'], this.selectedBanner['id']).
      subscribe(response => {
        if (response) {
          this.banner = response && response.length > 0 ? response[0] : {};
          this.initialBanner = Object.assign({}, this.banner);
          this.bannerForm = this.fb.group(
            {
              title: new FormControl(this.banner.title, [Validators.required]),
              category: new FormControl(this.banner.category),
              content: new FormControl(this.banner.content, [Validators.required]),
              contentType: new FormControl(this.banner.contentType, [Validators.required]),
              bannerType: new FormControl(this.banner.bannerType, [Validators.required])
            });
          this.bannerForm.controls['title'].disable();
        }
      });
  }

  addBanner(bannerObj: Banner) {
    bannerObj.status = 'Draft';
    this.bannerService.insertBanner(bannerObj, this.selectedApp).subscribe(response => {
      if (response) {
        this.mode = 'view';
        this.bannerForm.disable();
        this.showAlertMessages('success', TCCPConstantValues.admin.messages.BANNER_SAVE_SUCCESS_MESSAGE);
      }
    });
  }

  updateBanner(bannerObj: Banner) {
    this.bannerService.updateBanner(bannerObj, this.banner.id,
      this.selectedApp).subscribe(response => {
        if (response) {
          this.mode = 'view';
          this.showAlertMessages('success', TCCPConstantValues.admin.messages.BANNER_SAVE_SUCCESS_MESSAGE);
        }
      });
  }

  onSave(bannerForm) {
    this.bannerForm.markAllAsTouched();
    this.isSubmit = true;
    this.alerts = [];
    if (bannerForm.valid) {
      const requestData: Banner = < Banner > {};
      requestData.id = this.banner.id;
      requestData.content = bannerForm.controls.content.value;
      requestData.division = this.selectedApp;
      requestData.contentType = bannerForm.controls.contentType.value;
      requestData.bannerType = bannerForm.controls.bannerType.value;
      requestData.title = bannerForm.controls.title.value;
      requestData.category = bannerForm.controls.category.value; // update value
      requestData.status = this.banner.status;
      if (this.mode === 'add') {
        requestData.createdBy = this.authService.instance.getActiveAccount().username;
        requestData.createdAt = '';
        requestData.id = null;
        this.isSubmit = false ;
        this.addBanner(requestData);
      } else if (this.mode === 'edit') {
        this.isSubmit = false ;
        requestData.updatedBy = this.authService.instance.getActiveAccount().username;
        requestData.updatedAt = '';
        this.updateBanner(requestData);
      }
    }
  }

  onEdit() {
    this.alerts = [];
    this.mode = 'edit';
    this.pageTitle = 'Edit Banner';
    this.bannerForm.enable();
  }

  onCancel() {
    this.alerts = [];
    this.openCancelDialog();
  }

  openCancelDialog(): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      width: '350px',
      data: TCCPConstantValues.admin.messages.LOST_UNSAVED_MESSAGE,
      id: 'admin-' + this.adminService.selectedApplication,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.banner = this.initialBanner;
        this.isSubmit = false ;
        this.bannerForm.markAsUntouched();
        if (this.mode === 'add') {
          this.bannerForm.reset();
          this.bannerForm = this.fb.group(
            {
              title: new FormControl(this.banner.title, [Validators.required]),
              category: new FormControl(this.banner.category),
              content: new FormControl(this.banner.content, [Validators.required]),
              contentType: new FormControl('Internal', [Validators.required]),
              bannerType: new FormControl('multi', [Validators.required])
            });
        } else {
          this.mode = 'view';
          this.setPageMode('view');
          this.bannerForm.disable();
        }
      }
    });
  }

  goToBannerList() {
    this.router.navigateByUrl('/admin/manage-banners');
  }

  autoSizeTextArea(event?) {
    if (event) {
      event.target.style.height = '0';
      event.target.style.height = `${event.target.scrollHeight + 25}px`;
    }
    else {
      const textEle = document.getElementById('autosize-textarea');
      textEle.style.height = '0';
      textEle.style.height = `${textEle.scrollHeight + 25}px`;
    }
  }
}
